import {
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Assignment,
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
  IndeterminateCheckBox,
  KeyboardArrowDown,
  KeyboardArrowRight,
  KeyboardArrowUp,
} from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid";
import Loading from "components/Loading";
import PageableTable from "components/PageableTable/PageableTable";
import { RootState } from "config/store";
import { format as dateFormat } from "date-fns";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { getOrderInfo, getOrderListByTenantId } from "shared/network/order.api";
import { createProduction, ProductionStatus } from "shared/network/production.api";
import { Company, Item, Order } from "shared/types";
import { useGetSessionStorageKey } from "views/Comment/function";
import { TableState } from "views/Company/CompanyList";

function getItemSummary(selectedOrders: Order[]) {
  return Object.values(
    selectedOrders
      .flatMap(order =>
        (order.relOrderItems || []).map(item => ({
          item: item.item,
          quantity: item.quantity,
          order: order,
        })),
      )
      .reduce((acc, curr) => {
        if (curr?.item?.isComposite) {
          if (!acc[curr.item.id]) {
            acc[curr.item.id] = { item: curr.item, quantity: 0, orders: [] };
          }
          acc[curr.item.id].quantity += curr.quantity;
          acc[curr.item.id].orders.push(curr.order);
          return acc;
        } else {
          return acc;
        }
      }, {} as Record<string, { item: Item; quantity: number; orders: Order[] }>),
  );
}

const Page1OrderSelect = ({
  activeStep,
  setActiveStep,
  groupCode,
  setGroupCode,
}: {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  groupCode: string;
  setGroupCode: Dispatch<SetStateAction<string>>;
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { account, selectedRelTenant } = useSelector((state: RootState) => state.authentication);

  const [sortState, setSortState] = useState("");
  const [tableState, setTableState] = useState<TableState>({
    page: parseInt(
      window.sessionStorage.getItem(useGetSessionStorageKey("rap-order-list-page-number")) ||
        JSON.stringify(0),
    ),
    pageSize: parseInt(
      window.sessionStorage.getItem(useGetSessionStorageKey("rap-order-list-page-size")) ||
        JSON.stringify(10),
    ),
    filterOpen: false,
    filterValue: "",
  });
  const { page, pageSize, filterValue } = tableState;

  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const { watch, setValue } = useForm<{ company: Company }>();
  const selectedCompany = watch("company");
  const itemSummary = getItemSummary(selectedOrders);

  const fullOrderListQuery = useQuery(
    [
      "getOrderListByCompanyIdQuery",
      page,
      pageSize,
      selectedRelTenant.tenant.id,
      filterValue,
      sortState,
      selectedCompany,
    ],
    async () => {
      const hasInvoiceSentFilter = filterValue.includes("invoiceSent:");
      let newFilterValue = filterValue;
      if (hasInvoiceSentFilter) {
        newFilterValue = "";
        const idx = filterValue.search(/invoiceSent:(.+);/);
        const endIdx = filterValue.indexOf(";", idx);
        filterValue.substr(idx, endIdx);
        newFilterValue = `${filterValue.substring(0, idx === 0 ? 0 : idx)}${filterValue.substring(
          endIdx,
        )}`;
      }

      const { data } = await getOrderListByTenantId(
        page,
        pageSize,
        selectedRelTenant.tenant.id,
        `status:${"NEW"};` + newFilterValue,
        sortState,
      );
      return data;
    },
  );

  const columns: GridColDef[] = [
    {
      field: "multiSelect",
      headerName: " ",
      width: 50,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        if (selectedOrders?.length === fullOrderListQuery.data?.page?.content?.length) {
          return (
            <IconButton size="small" color="primary" onClick={() => setSelectedOrders([])}>
              <CheckBoxOutlined />
            </IconButton>
          );
        } else {
          return (
            <IconButton
              size="small"
              color="primary"
              onClick={() => setSelectedOrders(fullOrderListQuery.data?.page?.content || [])}
            >
              {selectedOrders?.length === 0 ? (
                <CheckBoxOutlineBlankOutlined />
              ) : (
                <IndeterminateCheckBox />
              )}
            </IconButton>
          );
        }
      },
      renderCell: ({ row }: GridRenderCellParams) => (
        <Box display="flex" justifyContent="flex-end" width="100%">
          {selectedOrders.find(value => value?.id === row.id) ? (
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                setSelectedOrders(prevState => [...prevState].filter(entry => entry?.id !== row.id))
              }
            >
              <CheckBoxOutlined />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              color="primary"
              onClick={() => setSelectedOrders(prevState => [...prevState, row as Order])}
            >
              <CheckBoxOutlineBlankOutlined />
            </IconButton>
          )}
        </Box>
      ),
    },
    {
      field: "orderId",
      headerName: t("order.orderId"),
      flex: 1,
      type: "stringContainsNumber",
      valueGetter: ({ row }: GridValueGetterParams) => row.orderId,
    },
    {
      field: "orderDate",
      headerName: t("order.orderDate"),
      sortable: false,
      flex: 1,
      valueGetter: ({ row }: GridValueGetterParams) =>
        row.createdOn ? dateFormat(new Date(row.createdOn), "yyyy. MM. dd. HH:mm") : "-",
    },
    {
      field: "actions",
      headerName: " ",
      width: 224,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <Box>
            <Tooltip title={t("common:button.show").toString()}>
              <IconButton
                component={Link}
                size="small"
                color="primary"
                style={{ margin: "0 8px" }}
                to={`/order/list/details?id=${row.id}`}
                target="_blank"
              >
                <Assignment />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    setValue(
      "company",
      !!account.user.relUserCompany ? account?.user?.relUserCompany?.[0]?.company : null,
    );
  }, [account, setValue]);

  const [nextLoading, setNextLoading] = useState(false);

  const orderInfoQuery = useQuery(["orderInfoQuery", selectedRelTenant.tenant.id], async () => {
    const { data } = await getOrderInfo(20, selectedRelTenant?.tenant?.id);

    return data.ongoingOrderItems;
  });

  return (
    <>
      <Loading open={nextLoading} />
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={4}
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Card style={{ marginBottom: 8 }}>
            <Box p={2} display="flex" alignItems="center">
              <Typography variant="h6" style={{ fontSize: 18, paddingBottom: 8 }}>
                Legtöbbet rendelt termékek
              </Typography>
            </Box>

            <Box style={{ maxHeight: "75vh", overflowY: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      Termék
                    </TableCell>
                    <TableCell
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                      align="right"
                    >
                      Mennyiség
                    </TableCell>
                    <TableCell
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 2,
                      }}
                    >
                      {" "}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderInfoQuery.isLoading ? (
                    [...Array(5)].map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Skeleton width="80%" />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width="40%" style={{ marginLeft: "auto" }} />
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ))
                  ) : orderInfoQuery.data && orderInfoQuery.data.length > 0 ? (
                    orderInfoQuery.data.map((order: any) => (
                      <TableRow key={order.item.id}>
                        <TableCell>{order.item.name}</TableCell>
                        <TableCell align="right">
                          {order.orderQuantity}{" "}
                          {order.item?.productUnit === "OWN"
                            ? order.item?.customUnitTypeName
                            : order.item?.productUnit
                            ? t(`item.unit.${order.item?.productUnit}`)
                            : ""}
                        </TableCell>
                        <TableCell>
                          {" "}
                          <Tooltip title={t("common:button.next")}>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={async () => {
                                setNextLoading(true);
                                try {
                                  const { data } = await createProduction(
                                    [
                                      {
                                        production: {
                                          item: order.item,
                                          quantity: order.orderQuantity,
                                          status: "CREATED" as ProductionStatus,
                                          tenantId: selectedRelTenant?.tenant?.id,
                                        },
                                        relProductionItemEntries: [],
                                      },
                                    ],
                                    selectedRelTenant?.tenant?.id,
                                  );
                                  setActiveStep(1);
                                  if (data?.item) {
                                    setGroupCode(data?.item);
                                    history.push(`/production/create?groupCode=${data?.item}`);
                                  }
                                  enqueueSnackbar(t("production.createSuccess"), {
                                    variant: "success",
                                  });
                                } catch (e) {
                                  enqueueSnackbar(t("production.createError"), {
                                    variant: "error",
                                  });
                                }
                                setNextLoading(false);
                              }}
                            >
                              <KeyboardArrowRight />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Nincs adat
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          sm={8}
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Card style={{ marginBottom: 8, padding: 16 }}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sm={6}
                style={{ height: "100%", display: "flex", flexDirection: "column" }}
              >
                <Typography variant="h6" style={{ fontSize: 18, paddingBottom: 8 }}>
                  Új megrendelések
                </Typography>
                <Box flex={1} minHeight={0} display="flex" flexDirection="column">
                  <PageableTable
                    sortState={sortState}
                    setSortState={setSortState}
                    sessionStorageKey="order-list"
                    filterable={["orderId"]}
                    tableState={tableState}
                    setTableState={setTableState}
                    columns={columns}
                    query={fullOrderListQuery}
                    minWidth={{ value: "sm" }}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6" style={{ fontSize: 18, paddingBottom: 8 }}>
                  {t("production.selectedItemsSummary")}
                </Typography>
                {selectedOrders.length > 0 && (
                  <Box
                    flex={1}
                    minHeight={0}
                    display="flex"
                    flexDirection="column"
                    style={{ overflowY: "auto" }}
                  >
                    <Box display="flex" flexDirection="column">
                      {itemSummary?.map((item, idx) => (
                        <Card
                          key={idx}
                          variant="outlined"
                          style={{ marginBottom: 8, padding: "8px 12px" }}
                        >
                          <Typography style={{ fontWeight: "bold" }}>{item.item.name}</Typography>
                          <Typography color="textSecondary" style={{ fontSize: 12 }}>
                            <span style={{ fontWeight: "bold" }}>{t("item.quantity")}: </span>
                            {item.quantity}{" "}
                            {item?.item?.productUnit === "OWN"
                              ? item?.item?.customUnitTypeName
                              : t(`item.unit.${item?.item?.productUnit}`)}
                          </Typography>
                          <Box>
                            <Typography color="textSecondary" style={{ fontSize: 12 }}>
                              <span style={{ fontWeight: "bold" }}>Megrendelés: </span>
                              {item.orders
                                .map(order => order.orderId)
                                .filter((v, i, a) => a.indexOf(v) === i)
                                .join(", ")}
                            </Typography>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  fullWidth
                  endIcon={<KeyboardArrowRight />}
                  disabled={!itemSummary?.length}
                  onClick={async () => {
                    setNextLoading(true);
                    try {
                      const temp = itemSummary.map(item => {
                        return {
                          production: {
                            item: item.item,
                            quantity: item.quantity,
                            status: "CREATED" as ProductionStatus,
                            tenantId: selectedRelTenant?.tenant?.id,
                          },
                          orderEntries: item.orders,
                          relProductionItemEntries: [],
                        };
                      });
                      const { data } = await createProduction(temp, selectedRelTenant?.tenant?.id);
                      setActiveStep(1);
                      if (data?.item) {
                        setGroupCode(data?.item);
                        history.push(`/production/create?groupCode=${data?.item}`);
                      }
                      enqueueSnackbar(t("production.createSuccess"), { variant: "success" });
                    } catch (e) {
                      enqueueSnackbar(t("production.createError"), { variant: "error" });
                    }
                    setNextLoading(false);
                  }}
                >
                  {t("common:button.next")}
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default Page1OrderSelect;
